import { z } from 'zod';

/**
 * Schemas Zod du module Équipements (Phase 10 — US-EQ-01 à 12 + US-SVC-07).
 *
 * Couvre :
 *  - CRUD équipement (US-EQ-02) — code, désignation, type, statut, criticité, localisation.
 *  - Scan QR / code-barres (US-EQ-03).
 *  - Affectation utilisateur avec workflow d'acceptation (US-EQ-04, US-EQ-05).
 *  - Affectation service / localisation + mouvements (US-EQ-06).
 *  - Maintenance préventive/curative (US-EQ-08).
 *  - Métrologie / étalonnages (US-EQ-09).
 *  - Documents (US-EQ-10) — réutilise documents polymorphes.
 *  - Filtres sauvegardés (US-EQ-12).
 *  - Équipements critiques d'un service (US-SVC-07).
 */

// ─── Constantes ─────────────────────────────────────────────────────────────

export const EQUIPEMENT_TYPES = ['production', 'technique', 'informatique', 'mobilier'] as const;
export type EquipementType = (typeof EQUIPEMENT_TYPES)[number];

export const EQUIPEMENT_STATUTS = [
  'en_service',
  'en_panne',
  'en_maintenance',
  'reforme',
  'en_stock',
] as const;
export type EquipementStatut = (typeof EQUIPEMENT_STATUTS)[number];

export const EQUIPEMENT_CRITICITES = ['vital', 'critique', 'important', 'standard'] as const;
export type EquipementCriticite = (typeof EQUIPEMENT_CRITICITES)[number];

export const EQUIPEMENT_STATUTS_AFFECTATION = [
  'non_affecte',
  'en_attente_acceptation',
  'affecte',
  'refuse',
] as const;
export type EquipementStatutAffectation = (typeof EQUIPEMENT_STATUTS_AFFECTATION)[number];

export const AFFECTATION_TYPES = [
  'service',
  'utilisateur',
  'desaffectation',
  'localisation',
  'acceptation',
  'refus',
] as const;
export type AffectationType = (typeof AFFECTATION_TYPES)[number];

export const INTERVENTION_MAINTENANCE_TYPES = ['preventive', 'curative', 'controle'] as const;
export type InterventionMaintenanceType = (typeof INTERVENTION_MAINTENANCE_TYPES)[number];

export const INTERVENTION_MAINTENANCE_STATUTS = [
  'planifiee',
  'en_cours',
  'terminee',
  'annulee',
] as const;
export type InterventionMaintenanceStatut = (typeof INTERVENTION_MAINTENANCE_STATUTS)[number];

export const ETALONNAGE_RESULTATS = ['conforme', 'non_conforme', 'ajuste'] as const;
export type EtalonnageResultat = (typeof ETALONNAGE_RESULTATS)[number];

export const SAVED_FILTER_MODULES = [
  'equipements',
  'interventions',
  'reservations',
  'personnels',
] as const;
export type SavedFilterModule = (typeof SAVED_FILTER_MODULES)[number];

// ─── Équipement CRUD (US-EQ-02 / US-EQ-12) ──────────────────────────────────

export const createEquipementSchema = z.object({
  code_equipement: z.string().min(1).max(50),
  numero_inventaire: z.string().max(50).optional().nullable(),
  code_barres: z.string().max(100).optional().nullable(),
  numero_serie: z.string().max(100).optional().nullable(),
  designation: z.string().min(1).max(255),
  type: z.enum(EQUIPEMENT_TYPES),
  statut: z.enum(EQUIPEMENT_STATUTS).default('en_service'),
  criticite: z.enum(EQUIPEMENT_CRITICITES).optional().nullable(),
  fabricant: z.string().max(255).optional().nullable(),
  modele: z.string().max(255).optional().nullable(),
  service_id: z.string().uuid().optional().nullable(),
  batiment_id: z.string().uuid().optional().nullable(),
  etage_id: z.string().uuid().optional().nullable(),
  salle_id: z.string().uuid().optional().nullable(),
  responsable_id: z.string().uuid().optional().nullable(),
  est_mobile: z.boolean().default(false),
  custom_fields: z.record(z.string(), z.unknown()).optional(),
});
export type CreateEquipementInput = z.infer<typeof createEquipementSchema>;

export const updateEquipementSchema = createEquipementSchema.partial();
export type UpdateEquipementInput = z.infer<typeof updateEquipementSchema>;

export const listEquipementsQuerySchema = z.object({
  /** Search full-text sur designation / code / numero_inventaire / numero_serie. */
  search: z.string().trim().max(255).optional(),
  type: z.enum(EQUIPEMENT_TYPES).optional(),
  statut: z.enum(EQUIPEMENT_STATUTS).optional(),
  criticite: z.enum(EQUIPEMENT_CRITICITES).optional(),
  statut_affectation: z.enum(EQUIPEMENT_STATUTS_AFFECTATION).optional(),
  service_id: z.string().uuid().optional(),
  batiment_id: z.string().uuid().optional(),
  etage_id: z.string().uuid().optional(),
  salle_id: z.string().uuid().optional(),
  responsable_id: z.string().uuid().optional(),
  utilisateur_affecte_id: z.string().uuid().optional(),
  est_mobile: z.coerce.boolean().optional(),
  sort_by: z
    .enum(['code_equipement', 'designation', 'type', 'statut', 'criticite', 'created_at'])
    .default('designation'),
  sort_order: z.enum(['asc', 'desc']).default('asc'),
  page: z.coerce.number().int().min(1).default(1),
  page_size: z.coerce.number().int().min(1).max(200).default(50),
});
export type ListEquipementsQuery = z.infer<typeof listEquipementsQuerySchema>;

// ─── Affectations service / localisation (US-EQ-06) ─────────────────────────

export const affecterServiceSchema = z.object({
  service_id: z.string().uuid().nullable(),
  /** Localisation optionnelle (peut être appliquée en même temps). */
  batiment_id: z.string().uuid().optional().nullable(),
  etage_id: z.string().uuid().optional().nullable(),
  salle_id: z.string().uuid().optional().nullable(),
  motif: z.string().min(1).max(1000),
});
export type AffecterServiceInput = z.infer<typeof affecterServiceSchema>;

export const affecterLocalisationSchema = z.object({
  batiment_id: z.string().uuid().nullable(),
  etage_id: z.string().uuid().optional().nullable(),
  salle_id: z.string().uuid().optional().nullable(),
  motif: z.string().min(1).max(1000),
});
export type AffecterLocalisationInput = z.infer<typeof affecterLocalisationSchema>;

export const createMouvementSchema = z.object({
  destination_batiment_id: z.string().uuid().optional().nullable(),
  destination_etage_id: z.string().uuid().optional().nullable(),
  destination_salle_id: z.string().uuid().optional().nullable(),
  motif: z.string().max(1000).optional().nullable(),
  observations: z.string().max(2000).optional().nullable(),
});
export type CreateMouvementInput = z.infer<typeof createMouvementSchema>;

// ─── Workflow affectation utilisateur (US-EQ-04 / US-EQ-05) ─────────────────

export const demanderAffectationUtilisateurSchema = z.object({
  utilisateur_id: z.string().uuid(),
  motif: z.string().min(1).max(1000),
});
export type DemanderAffectationUtilisateurInput = z.infer<
  typeof demanderAffectationUtilisateurSchema
>;

export const repondreAffectationSchema = z.object({
  accept: z.boolean(),
  motif_refus: z.string().max(1000).optional().nullable(),
  commentaire: z.string().max(2000).optional().nullable(),
});
export type RepondreAffectationInput = z.infer<typeof repondreAffectationSchema>;

export const signalerProblemeAffectationSchema = z.object({
  motif: z.string().min(1).max(1000),
  commentaire: z.string().max(2000).optional().nullable(),
});
export type SignalerProblemeAffectationInput = z.infer<typeof signalerProblemeAffectationSchema>;

// ─── Interventions maintenance (US-EQ-08) ───────────────────────────────────

export const upsertInterventionMaintenanceSchema = z.object({
  date_intervention: z.string().datetime(),
  type_intervention: z.enum(INTERVENTION_MAINTENANCE_TYPES),
  duree_intervention: z.coerce.number().int().min(0).optional().nullable(),
  technicien_id: z.string().uuid().optional().nullable(),
  description: z.string().max(2000).optional().nullable(),
  cout_intervention: z.coerce.number().min(0).optional().nullable(),
  prochaine_intervention: z.string().date().optional().nullable(),
  statut: z.enum(INTERVENTION_MAINTENANCE_STATUTS).default('planifiee'),
  observations: z.string().max(4000).optional().nullable(),
  /** Intervalle en jours utilisé si type='preventive' pour calculer prochaine_intervention si absente. */
  interval_days: z.coerce.number().int().min(1).optional().nullable(),
  custom_fields: z.record(z.string(), z.unknown()).optional(),
});
export type UpsertInterventionMaintenanceInput = z.infer<
  typeof upsertInterventionMaintenanceSchema
>;

export const listMaintenancesUpcomingQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(365).default(30),
});
export type ListMaintenancesUpcomingQuery = z.infer<typeof listMaintenancesUpcomingQuerySchema>;

// ─── Étalonnages / métrologie (US-EQ-09) ────────────────────────────────────

export const upsertEtalonnageSchema = z.object({
  date_etalonnage: z.string().date(),
  organisme: z.string().min(1).max(255),
  resultat: z.enum(ETALONNAGE_RESULTATS),
  prochain_etalonnage: z.string().date().optional().nullable(),
  document_url: z.string().max(1000).optional().nullable(),
  observations: z.string().max(4000).optional().nullable(),
  custom_fields: z.record(z.string(), z.unknown()).optional(),
});
export type UpsertEtalonnageInput = z.infer<typeof upsertEtalonnageSchema>;

export const listEtalonnagesUpcomingQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(365).default(30),
});
export type ListEtalonnagesUpcomingQuery = z.infer<typeof listEtalonnagesUpcomingQuerySchema>;

// ─── Équipements critiques (US-SVC-07) ──────────────────────────────────────

export const createEquipementCritiqueSchema = z.object({
  equipement_id: z.string().uuid(),
  niveau_criticite: z.enum(EQUIPEMENT_CRITICITES),
  priorite_remplacement: z.coerce.number().int().min(0).optional().nullable(),
});
export type CreateEquipementCritiqueInput = z.infer<typeof createEquipementCritiqueSchema>;

// ─── Filtres sauvegardés (US-EQ-12) ─────────────────────────────────────────

export const createSavedFilterSchema = z.object({
  module: z.enum(SAVED_FILTER_MODULES),
  nom: z.string().min(1).max(100),
  filtres: z.record(z.string(), z.unknown()),
  is_default: z.boolean().default(false),
});
export type CreateSavedFilterInput = z.infer<typeof createSavedFilterSchema>;

export const updateSavedFilterSchema = z.object({
  nom: z.string().min(1).max(100).optional(),
  filtres: z.record(z.string(), z.unknown()).optional(),
  is_default: z.boolean().optional(),
});
export type UpdateSavedFilterInput = z.infer<typeof updateSavedFilterSchema>;

export const listSavedFiltersQuerySchema = z.object({
  module: z.enum(SAVED_FILTER_MODULES).optional(),
});
export type ListSavedFiltersQuery = z.infer<typeof listSavedFiltersQuerySchema>;

// ─── Scan QR / code-barres (US-EQ-03) ───────────────────────────────────────

export const scanQuerySchema = z.object({
  code: z.string().min(1).max(255),
});
export type ScanQueryInput = z.infer<typeof scanQuerySchema>;
